import { useCallback } from 'react';
import { IDynamicProjectGetData, IProject } from '../../../api/IApiResponse';
import { EProjectType, unit as unitEnum } from '../../../enum/project';
import * as ProjectTypeService from '../../../services/ProjectTypeService';

/**
 * Hook for transforming project data from API to internal format
 * Single responsibility: Data transformation and mapping
 *
 * NOTE: The data transformation (NESTED → FLAT) already happens in DynamicProjectService.getProject
 * via transformProjectFromBackend. This hook just maps additional fields for Redux.
 */
export const useProjectTransform = () => {
    const transformProjectData = useCallback((apiData: IDynamicProjectGetData): IProject => {
        // Data is already transformed to FLAT format by DynamicProjectService.getProject
        // No need to call transformProjectFromBackend again - it would cause type errors
        const flattenedApiData = apiData;

        const projectInfoData = flattenedApiData.projectInfo.data;

        const { type, method } = ProjectTypeService.getDataTypeByProject(projectInfoData);

        const mappedUnit = flattenedApiData.unit === unitEnum.SI ? unitEnum.SI : unitEnum.Imperial;

        const result = {
            ...flattenedApiData,
            unit: mappedUnit,
            // Initialize conversion tracking for API data
            originalUnit: unitEnum.SI, // API data is ALWAYS metric
            conversionApplied: false, // Fresh data from API hasn't been converted yet
            conversionSource: 'api', // Mark that this data came from API
            type: type || EProjectType.container_yard,
            method: method || '',
            guid: flattenedApiData.guid || '',
            projectName: flattenedApiData.projectName || '',
            idApp: typeof flattenedApiData.idApp === 'number' ? flattenedApiData.idApp : Number(process.env.REACT_APP_ID_APP) || 42,
            idUser: typeof flattenedApiData.idUser === 'number' ? flattenedApiData.idUser : 0,
            creationDate: flattenedApiData.creationDate || new Date().toISOString(),
            lastModified: flattenedApiData.lastModified || new Date().toISOString(),
            lastSeen: flattenedApiData.lastSeen || new Date().toISOString(),
            status: typeof flattenedApiData.status === 'number' ? flattenedApiData.status : 0,
            sharedUsers: Array.isArray(flattenedApiData.sharedUsers) ? flattenedApiData.sharedUsers : [],
            projectInfo: flattenedApiData.projectInfo || { coordinates: [0, 0], projectOwner: '', data: {} },
            hasBeenRan: flattenedApiData.hasBeenRan || false,
            geogridHasBeenRan: flattenedApiData.geogridHasBeenRan || false,
            readOnly: flattenedApiData.readOnly || false,
            disableRun: flattenedApiData.disableRun || false,
            newProject: false, // This is for existing projects
        } as IProject;

        return result;
    }, []);

    return {
        transformProjectData,
    };
};

export type UseProjectTransformReturn = ReturnType<typeof useProjectTransform>;
